import React, { memo, useContext, useEffect, useState } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { sortByDate } from "@utils/sorters";
import ObjectPreview from "./ObjectPreview";
import { formatDate, isValidDate } from "@utils/dates";
import { dueDateClassForTask } from "@utils/dueDateClassForTask";
import { groupBy } from "@utils/groupBy";
import { Task } from "@components/Task";
import { FauxTable, FauxTableRow } from "@components/FauxTable";
import { formatCurrency } from "@utils/numbers";
import { useParams, useSearchParams } from "react-router-dom";

interface ColumnWithValue {
  label: string;
  value: string;//|number|null;
  transformer?: never;
}
interface ColumnWithTransformer {
  label: string;
  value?: never;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const ObjectRelationshipTasks = memo(function ObjectRelationshipTasks({
  relationship = [],
  title,
  tint = null,
  // columns,
  collapsible = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const jumpToTask = searchParams.get('task');
  useEffect(() => {
    if(jumpToTask) {
      setTimeout(() => {
        const taskRow = document.querySelector(`#task-${jumpToTask}`);
        taskRow?.scrollIntoView({ behavior: "smooth", /*block: "end", inline: "nearest"*/ });
      }, 100);
    }
  }, [jumpToTask]);

  const perPage = 5;

  // const groupedRelationship = groupBy(relationship, ({category_name}) => category_name);

  const sortedRelationship = relationship.sort((a, b) => {
    return sortByDate(a, b, 'due_date', 'asc');
  });

  /*
  $scope.nullsToTop = function(obj) {
    return (obj.completed_at ? 0: -1);
  };
*/

  // .sort((a, b) => sortByDate(a, b, 'due_date', 'asc'))

  // task in object.tasks | orderBy: [nullsToTop, 'due_date']

  const canCollapse = collapsible && relationship.length > perPage;
  const isEmpty = relationship.length === 0;

  return (
    <ObjectRelationshipCard
      unpadded
      empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 1.5,
              pointerEvents: canCollapse ? 'all' : 'none',
              cursor: 'pointer',
              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={0.5}
            >
              <Typography sx={theme => ({flex: 1, fontWeight: '600', color: 'white' })}>{title}</Typography>
            </Stack>
          </CardActions>
        </CardOverflow>
      }
      sx={{
        '--mm-card-height': 'auto',
      }}
    >
      {/* <CardOverflow> */}
      <Table
        stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
          'thead th': {
            borderRadius: '0 !important',
          },
        }}
      >
        <thead>
          <tr>
            <th>Task</th>
            <th style={{width: '15%'}}>Due</th>
            <th style={{width: '15%'}}>Budget</th>
            <th style={{width: '35%'}}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {sortedRelationship.map((task, taskIndex) => {
            const isValidDueDate = isValidDate(task.due_date);
            const dueDate = isValidDate(task.due_date) ? formatDate(task.due_date) : null;
            const isCompleted = task.completed_at && isValidDate(task.completed_at);

            return (
              <tr key={taskIndex} id={`task-${task.id}`} className={isCompleted ? 'task-complete' : ''}>
                <td>
                  <Task task={task}/>
                </td>
                <td className={`${dueDateClassForTask(task)}`}>
                  {isValidDueDate && (<time className={isCompleted ? 'task-complete' : ''}>{dueDate}</time>)}
                  {!isValidDueDate && (<>N/A</>)}
                </td>
                <td>
                  <FauxTable>
                    <FauxTableRow title={'Type'}>
                      {task.budget_type == 1 ? 'Income' : 'Expense'}
                    </FauxTableRow>
                    <FauxTableRow title={'Est.'}>{formatCurrency(task.estimated_budget)}</FauxTableRow>
                    <FauxTableRow title={'Actual'}>{formatCurrency(task.actual_budget)}</FauxTableRow>
                  </FauxTable>
                </td>
                <td>{task.notes}</td>
              </tr>
            )
          })}
        </tbody>

        {/*
        {Object.entries(sortedRelationship).map(([category, involvements], index:number) => {
          return (<React.Fragment key={index}>
            <thead>
              <tr>
                <th colSpan={2}>{category}</th>
              </tr>
            </thead>
            <tbody>
              {involvements.map((involvement, involvementIndex:number) => {
                return (<React.Fragment key={involvementIndex}>
                  <tr>
                    <td>
                      <Tooltip title={involvement.description} placement="left">
                        <Typography fontSize={'sm'}>
                          {involvement.name}
                        </Typography>
                      </Tooltip>
                      {involvement.pivot.notes && <Typography fontSize={'sm'} sx={{fontSize: '0.75rem'}}>{involvement.pivot.notes}</Typography>}
                    </td>
                    <td>{involvement.pivot.date}</td>
                  </tr>
                </React.Fragment>)
              })}
            </tbody>
          </React.Fragment>);
        })}
        */}
      </Table>
      {/* </CardOverflow> */}

    </ObjectRelationshipCard>
  );
});

export default ObjectRelationshipTasks;
